import React from "react";
import { useTranslation } from "react-i18next";
import { Column, Row, ColumnDef } from "@tanstack/react-table";
import { DataTable } from "@/components/DataTable";
import {
  MenubarContent,
  MenubarItem,
  Menubar,
  MenubarTrigger,
  MenubarMenu,
} from "@/components/ui";
import { MoreVertical } from "lucide-react";
import {
  LiveEvent,
  LiveEventTypeEnum,
  LiveEventAudienceEnum,
} from "@/__generated__/types";
import { useLocale } from "@/hooks/useLocale";
import { formatDate } from "@/lib/formatDate";
import { timestampToDate } from "@/util/dateUtils";
import { DestroyEventButton } from "../DestroyEventButton";

const DATE_FORMAT = "PPPp";
const PLACEHOLDER_FALLBACK = "--";

export const useScheduledEventsColumns = (): ColumnDef<LiveEvent>[] => {
  const { t } = useTranslation();
  const locale = useLocale();

  // const handleEditClick = (event: LiveEvent) => {
  //   console.log("Navigate to edit event:", event.id);
  // };

  const getEventTypeLabel = (eventType: string) => {
    switch (eventType) {
      case LiveEventTypeEnum.LiveEvent:
        return t("gamesAndLiveEvents.table.eventTypes.liveEvent");
      case LiveEventTypeEnum.BotEvent:
        return t("gamesAndLiveEvents.table.eventTypes.automatedGame");
      default:
        return eventType;
    }
  };

  const getAudienceLabel = (audience: string) => {
    switch (audience) {
      case LiveEventAudienceEnum.Everyone:
        return t("gamesAndLiveEvents.table.audience.everyone");
      case LiveEventAudienceEnum.Applicants:
        return t("gamesAndLiveEvents.table.audience.applicants");
      case LiveEventAudienceEnum.Admits:
        return t("gamesAndLiveEvents.table.audience.admits");
      case LiveEventAudienceEnum.Commits:
        return t("gamesAndLiveEvents.table.audience.commits");
      case LiveEventAudienceEnum.Other:
        return t("gamesAndLiveEvents.table.audience.other");
      default:
        return audience || "-";
    }
  };

  const columns: ColumnDef<LiveEvent>[] = [
    {
      id: "name",
      accessorFn: (row: LiveEvent) => row?.name || "",
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<LiveEvent> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("gamesAndLiveEvents.table.scheduled.columns.eventName")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<LiveEvent> }) => (
        <DataTable.TBodyCell>
          <div className="w-80 overflow-hidden text-ellipsis">
            {row.original?.name || PLACEHOLDER_FALLBACK}
          </div>
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "eventType",
      accessorFn: (row: LiveEvent) => getEventTypeLabel(row?.eventType || ""),
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<LiveEvent> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("gamesAndLiveEvents.table.scheduled.columns.eventType")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<LiveEvent> }) => (
        <DataTable.TBodyCell>
          {row.original
            ? getEventTypeLabel(row.original.eventType)
            : PLACEHOLDER_FALLBACK}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "startsAt",
      accessorFn: (row: LiveEvent) => {
        const { startsAt } = row || {};
        return startsAt
          ? formatDate(timestampToDate(startsAt * 1000), DATE_FORMAT, {
              locale: locale as "en",
            })
          : "";
      },
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<LiveEvent> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("gamesAndLiveEvents.table.scheduled.columns.startTime")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<LiveEvent> }) => (
        <DataTable.TBodyCell>
          {row.original?.startsAt
            ? formatDate(
                timestampToDate(row.original?.startsAt * 1000),
                DATE_FORMAT,
                {
                  locale: locale as "en",
                },
              )
            : PLACEHOLDER_FALLBACK}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "audience",
      accessorFn: (row: LiveEvent) => row?.audience || "",
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<LiveEvent> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("gamesAndLiveEvents.table.scheduled.columns.audience")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<LiveEvent> }) => (
        <DataTable.TBodyCell>
          {row.original
            ? getAudienceLabel(row.original.audience)
            : PLACEHOLDER_FALLBACK}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "chatChannel",
      accessorFn: (row: LiveEvent) => row?.chatChannel?.displayName || "",
      enableSorting: true,
      enableColumnFilter: false,
      header: ({ column }: { column: Column<LiveEvent> }) => (
        <DataTable.THeaderCellSorting column={column}>
          {t("gamesAndLiveEvents.table.scheduled.columns.chatChannel")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<LiveEvent> }) => (
        <DataTable.TBodyCell>
          {row.original?.chatChannel?.displayName || PLACEHOLDER_FALLBACK}
        </DataTable.TBodyCell>
      ),
    },
    {
      id: "actions",
      accessorFn: () => "",
      enableSorting: false,
      enableColumnFilter: false,
      header: () => <DataTable.THeaderCell>{""}</DataTable.THeaderCell>,
      cell: ({ row }: { row: Row<LiveEvent> }) => {
        const event = row.original;
        if (!event) {
          return (
            <DataTable.TBodyCell>{PLACEHOLDER_FALLBACK}</DataTable.TBodyCell>
          );
        }

        return (
          <DataTable.TBodyCell isLastCell>
            <div className="flex items-center justify-end gap-2">
              {/* <Button
                variant="outline"
                size="sm"
                onClick={() => handleEditClick(event)}
              >
                {t("gamesAndLiveEvents.table.scheduled.actions.edit")}
              </Button> */}

              <Menubar className="p-0">
                <MenubarMenu>
                  <MenubarTrigger
                    role="menuitem"
                    aria-label={t("dataTable.openTableActionsMenu")}
                    aria-haspopup="true"
                    className="cursor-pointer p-2 hover:bg-muted"
                  >
                    <MoreVertical className="h-4 w-4" aria-hidden="true" />
                  </MenubarTrigger>
                  <MenubarContent
                    align="end"
                    role="menu"
                    data-testid="table-actions-menu"
                    aria-label={t("dataTable.tableActions")}
                    aria-orientation="vertical"
                  >
                    <MenubarItem
                      onClick={(e) => e.preventDefault()}
                      variant="destructive"
                      role="menuitem"
                      aria-label={t(
                        "gamesAndLiveEvents.table.scheduled.actions.delete",
                      )}
                    >
                      <DestroyEventButton event={event} />
                    </MenubarItem>
                  </MenubarContent>
                </MenubarMenu>
              </Menubar>
            </div>
          </DataTable.TBodyCell>
        );
      },
    },
  ];

  return columns;
};
